import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, FileSpreadsheet, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { trackEvent } from '@/lib/eventTracking';

/**
 * StickyWaitlistCTA - Global sticky CTA for Excel Template Waitlist
 * 
 * Features:
 * - Shows after 8-10 seconds delay
 * - Hidden on /template-excel page
 * - Closable with localStorage persistence
 * - Hidden if user already submitted waitlist
 * - Analytics tracking for view, click, close events
 */

// localStorage keys
const STORAGE_KEY_CLOSED = 'sticky_cta_closed';
const STORAGE_KEY_SUBMITTED = 'waitlist_submitted';

// Routes where sticky CTA should NOT appear
const HIDDEN_ROUTES = ['/template-excel', '/admin'];

// Delay before showing (in ms)
const SHOW_DELAY = 8000; // 8 seconds

export default function StickyWaitlistCTA() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const hasTrackedView = useRef(false);
  const showTimeoutRef = useRef(null);

  // Get current page info for analytics
  const getPageInfo = () => {
    const pathname = location.pathname;
    let pageType = 'page';
    if (pathname.startsWith('/calculators/')) pageType = 'calculator_detail';
    else if (pathname.startsWith('/ai-tools/')) pageType = 'ai_tool';
    else if (pathname.startsWith('/guides/')) pageType = 'guide';
    else if (pathname.startsWith('/news/')) pageType = 'news';
    else if (pathname === '/') pageType = 'homepage';
    
    const pageName = pathname === '/' ? 'home' : pathname.split('/').filter(Boolean).pop() || 'unknown';
    
    return { page_name: pageName, page_type: pageType };
  };

  // Check if CTA should be shown based on route and localStorage
  const shouldShowCTA = () => {
    // Check if on hidden route
    if (HIDDEN_ROUTES.some(route => location.pathname.startsWith(route))) {
      return false;
    }
    
    // Check localStorage for closed state
    if (typeof window !== 'undefined') {
      const isClosed = localStorage.getItem(STORAGE_KEY_CLOSED) === 'true';
      const hasSubmitted = localStorage.getItem(STORAGE_KEY_SUBMITTED) === 'true';
      if (isClosed || hasSubmitted) {
        return false;
      }
    }
    
    return true;
  };

  // Handle CTA click
  const handleCTAClick = () => {
    const pageInfo = getPageInfo();
    
    // Track click event
    trackEvent('sticky_cta_click', {
      ...pageInfo,
      cta_location: 'sticky_global',
      cta_name: 'dapatkan_gratis'
    });
    
    // Navigate to waitlist page
    navigate('/template-excel');
  };

  // Handle close button click
  const handleClose = () => {
    const pageInfo = getPageInfo();
    
    // Track close event
    trackEvent('sticky_cta_close', {
      ...pageInfo,
      cta_location: 'sticky_global'
    });
    
    // Save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY_CLOSED, 'true');
    }
    
    // Hide with animation
    setIsVisible(false);
    
    // Remove from DOM after animation
    setTimeout(() => {
      setShouldRender(false);
    }, 300);
  };

  // Track view event (only once per page load)
  const trackViewEvent = () => {
    if (hasTrackedView.current) return;
    hasTrackedView.current = true;
    
    const pageInfo = getPageInfo();
    trackEvent('sticky_cta_view', {
      ...pageInfo,
      cta_location: 'sticky_global'
    });
  };

  // Effect to handle visibility
  useEffect(() => {
    // Reset view tracking on route change
    hasTrackedView.current = false;
    
    // Clear any existing timeout
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    
    // Check if should show
    if (!shouldShowCTA()) {
      setIsVisible(false);
      setShouldRender(false);
      return;
    }
    
    // Set timeout to show after delay
    showTimeoutRef.current = setTimeout(() => {
      setShouldRender(true);
      // Small delay for animation
      requestAnimationFrame(() => {
        setIsVisible(true);
        trackViewEvent();
      });
    }, SHOW_DELAY);
    
    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
    };
  }, [location.pathname]);

  // Don't render if not needed
  if (!shouldRender) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-3 sm:p-4 transition-all duration-300 ease-out ${
        isVisible 
          ? 'translate-y-0 opacity-100' 
          : 'translate-y-full opacity-0'
      }`}
      data-testid="sticky-waitlist-cta"
    >
      <div className="max-w-lg mx-auto">
        <div className="relative bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl shadow-lg shadow-emerald-900/20 p-4 sm:p-5">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Close"
            data-testid="sticky-cta-close"
          >
            <X className="w-4 h-4 text-white" />
          </button>
          
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Icon */}
            <div className="hidden sm:flex w-12 h-12 bg-white/10 rounded-xl items-center justify-center flex-shrink-0">
              <FileSpreadsheet className="w-6 h-6 text-white" />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0 pr-6">
              <h3 className="text-white font-semibold text-sm sm:text-base leading-tight">
                Template Excel Gratis Saat Launch
              </h3>
              <p className="text-emerald-100 text-xs sm:text-sm mt-0.5">
                Gabung waitlist sekarang
              </p>
            </div>
            
            {/* CTA Button */}
            <Button
              onClick={handleCTAClick}
              className="flex-shrink-0 bg-white text-emerald-700 hover:bg-emerald-50 font-semibold text-xs sm:text-sm px-3 sm:px-4 py-2 h-auto rounded-xl shadow-sm"
              data-testid="sticky-cta-button"
            >
              <span className="hidden sm:inline">Dapatkan Gratis</span>
              <span className="sm:hidden">Gratis</span>
              <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Utility function to mark waitlist as submitted
 * Call this from TemplateExcelWaitlist after successful submission
 */
export function markWaitlistSubmitted() {
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY_SUBMITTED, 'true');
  }
}

/**
 * Utility function to reset sticky CTA state (for testing)
 */
export function resetStickyCTAState() {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY_CLOSED);
    localStorage.removeItem(STORAGE_KEY_SUBMITTED);
  }
}
